import React from 'react';
import { motion } from 'framer-motion';
const gameCards = [{
  name: 'Neon Blitz',
  gradient: 'from-[#FF2D2D]/60 to-[#CC1A1A]/30',
  matchScore: 0.95
}, {
  name: 'Gold Rush',
  gradient: 'from-amber-500/50 to-amber-700/20',
  matchScore: 0.82
}, {
  name: 'Cyber Spin',
  gradient: 'from-cyan-500/40 to-blue-700/20',
  matchScore: 0.71
}, {
  name: 'Pharaoh X',
  gradient: 'from-yellow-600/40 to-orange-800/20',
  matchScore: 0.88
}, {
  name: 'Arctic Wild',
  gradient: 'from-sky-400/40 to-indigo-700/20',
  matchScore: 0.64
}, {
  name: 'Inferno 7s',
  gradient: 'from-[#FF2D2D]/50 to-rose-800/20',
  matchScore: 0.93
}];
const playerSegments = [{
  label: 'High Rollers',
  y: 60
}, {
  label: 'Casual Explorers',
  y: 170
}, {
  label: 'Bonus Hunters',
  y: 280
}, {
  label: 'Tournament Players',
  y: 390
}];
function MatchRing({
  score,
  size = 72
}: {
  score: number;
  size?: number;
}) {
  const circumference = Math.PI * (size - 4);
  const strokeDashoffset = circumference * (1 - score);
  const glowIntensity = Math.floor(score * 25);
  return <svg width={size} height={size} className="absolute inset-0" data-id="element-223">
      <circle cx={size / 2} cy={size / 2} r={(size - 4) / 2} fill="none" stroke="#8A8A96" strokeWidth="1" opacity="0.15" data-id="element-224" />
      <motion.circle cx={size / 2} cy={size / 2} r={(size - 4) / 2} fill="none" stroke="#FF2D2D" strokeWidth="2" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{
      filter: `drop-shadow(0 0 ${glowIntensity}px rgba(255, 45, 45, ${score * 0.6}))`
    }} initial={{
      strokeDashoffset: circumference
    }} whileInView={{
      strokeDashoffset
    }} viewport={{
      once: true
    }} transition={{
      duration: 1.5,
      delay: 0.5,
      ease: 'easeOut'
    }} data-id="element-225" />
    </svg>;
}
function PathwayLines() {
  const paths = ['M180 40 H260 Q280 40 280 60 V60 H380', 'M180 40 H240 Q260 40 260 60 V170 H380', 'M180 120 H250 Q270 120 270 140 V170 H380', 'M180 120 H230 Q250 120 250 140 V280 H380', 'M180 200 H260 Q280 200 280 220 V280 H380', 'M180 280 H260 Q280 280 280 300 V390 H380', 'M180 360 H240 Q260 360 260 370 V390 H380'];
  return <>
      {paths.map((d, i) => <motion.path key={i} d={d} stroke="#FF2D2D" strokeWidth="1" fill="none" opacity="0.4" initial={{
      pathLength: 0
    }} whileInView={{
      pathLength: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1.2,
      delay: 0.8 + i * 0.15,
      ease: 'easeInOut'
    }} data-id="element-226" />)}
      {/* Animated pulse dots traveling along paths */}
      {paths.slice(0, 4).map((d, i) => <motion.circle key={`pulse-${i}`} r="3" fill="#FF2D2D" initial={{
      opacity: 0
    }} whileInView={{
      opacity: [0, 0.8, 0]
    }} viewport={{
      once: true
    }} transition={{
      duration: 2,
      delay: 2 + i * 0.3,
      repeat: Infinity,
      repeatDelay: 1
    }} data-id="element-227">
          <animateMotion dur="2s" repeatCount="indefinite" begin={`${i * 0.5}s`} data-id="element-228">
            <mpath href={`#pathway-${i}`} data-id="element-229" />
          </animateMotion>
        </motion.circle>)}
      {/* Hidden paths for animateMotion */}
      {paths.slice(0, 4).map((d, i) => <path key={`ref-${i}`} id={`pathway-${i}`} d={d} fill="none" stroke="none" data-id="element-230" />)}
    </>;
}
export function Protocol() {
  return <section id="protocol" className="relative w-full py-24 md:py-32 px-6 md:px-12 bg-[#050508]" data-id="element-231">
      {/* Section header */}
      <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20" data-id="element-232">
        <motion.h2 className="font-['Bebas_Neue'] text-5xl md:text-7xl tracking-wider text-[#C4C4CC] mb-4" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.7
      }} data-id="element-233">
          THE PROTOCOL
        </motion.h2>
        <motion.div className="w-24 h-[1px] bg-[#FF2D2D] mx-auto mb-6" initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-234" />
        <motion.p className="text-[#8A8A96] text-base md:text-lg font-light" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.4
      }} data-id="element-235">
          Where games find their perfect audience
        </motion.p>
      </div>

      {/* Visualization */}
      <motion.div className="max-w-5xl mx-auto relative" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 1
    }} data-id="element-236">
        {/* Desktop visualization */}
        <div className="hidden lg:block relative" style={{
        height: 460
      }} data-id="element-237">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 900 460" fill="none" preserveAspectRatio="xMidYMid meet" data-id="element-238">
            <PathwayLines data-id="element-239" />
          </svg>

          {/* Game cards - left side */}
          <div className="absolute left-0 top-0 grid grid-cols-2 gap-4" style={{
          width: 180
        }} data-id="element-240">
            {gameCards.map((game, i) => <motion.div key={game.name} className="relative w-[72px] h-[72px]" initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3 + i * 0.1
          }} data-id="element-241">
                <MatchRing score={game.matchScore} data-id="element-242" />
                <div className={`absolute inset-[6px] bg-gradient-to-br ${game.gradient} rounded-sm flex items-end p-1`} data-id="element-243">
                  <span className="text-[8px] text-[#C4C4CC] font-medium leading-tight truncate" data-id="element-244">
                    {game.name}
                  </span>
                </div>
              </motion.div>)}
          </div>

          {/* Player segment nodes - right side */}
          <div className="absolute right-0 top-0" style={{
          width: 200
        }} data-id="element-245">
            {playerSegments.map((segment, i) => <motion.div key={segment.label} className="absolute flex items-center gap-3" style={{
            top: segment.y - 20,
            right: 0
          }} initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.5 + i * 0.15
          }} data-id="element-246">
                <div className="w-10 h-10 rounded-full border border-[#8A8A96]/30 bg-[#0A0A0F] flex items-center justify-center relative" data-id="element-247">
                  <div className="w-3 h-3 rounded-full bg-[#FF2D2D]/60" data-id="element-248" />
                  <motion.div className="absolute inset-0 rounded-full border border-[#FF2D2D]/30" animate={{
                scale: [1, 1.4, 1],
                opacity: [0.3, 0, 0.3]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.5
              }} data-id="element-249" />
                </div>
                <span className="text-xs text-[#8A8A96] tracking-wider uppercase whitespace-nowrap" data-id="element-250">
                  {segment.label}
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* Mobile visualization - simplified */}
        <div className="lg:hidden" data-id="element-251">
          <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-252">
            {gameCards.map((game, i) => <motion.div key={game.name} className="relative w-full aspect-square max-w-[80px] mx-auto" initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2 + i * 0.08
          }} data-id="element-253">
                <MatchRing score={game.matchScore} size={80} data-id="element-254" />
                <div className={`absolute inset-[6px] bg-gradient-to-br ${game.gradient} rounded-sm flex items-end p-1.5`} data-id="element-255">
                  <span className="text-[9px] text-[#C4C4CC] font-medium leading-tight truncate" data-id="element-256">
                    {game.name}
                  </span>
                </div>
              </motion.div>)}
          </div>

          {/* Connector */}
          <div className="flex justify-center my-4" data-id="element-257">
            <svg width="2" height="40" viewBox="0 0 2 40" data-id="element-258">
              <motion.line x1="1" y1="0" x2="1" y2="40" stroke="#FF2D2D" strokeWidth="1" initial={{
              pathLength: 0
            }} whileInView={{
              pathLength: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-259" />
            </svg>
          </div>

          <div className="flex flex-wrap justify-center gap-3" data-id="element-260">
            {playerSegments.map((segment, i) => <motion.div key={segment.label} className="flex items-center gap-2 px-3 py-2 border border-[#8A8A96]/20 bg-[#0A0A0F]" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3 + i * 0.1
          }} data-id="element-261">
                <div className="w-2 h-2 rounded-full bg-[#FF2D2D]" data-id="element-262" />
                <span className="text-xs text-[#8A8A96] tracking-wider uppercase" data-id="element-263">
                  {segment.label}
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* Description */}
        <motion.p className="text-center text-[#8A8A96] text-sm md:text-base font-light max-w-2xl mx-auto mt-12 md:mt-16 leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.6
      }} data-id="element-264">
          Our protocol continuously analyzes behavioral micro-signals across
          12,000+ game titles, matching content to player segments with 98.2%
          accuracy. Every connection is scored, measured, and optimized in
          real-time.
        </motion.p>
      </motion.div>
    </section>;
}